import type { ImgHTMLAttributes } from "react";
import { useRouteLoaderData } from "react-router";
import type { Route } from "../+types/root";
import {
	buildResponsiveImageUrls,
	getImageDimensions,
	type ImageViewport,
} from "../utils/image-transform";

interface ProductImageProps
	extends Omit<
		ImgHTMLAttributes<HTMLImageElement>,
		"src" | "srcSet" | "sizes"
	> {
	src: string | null | undefined;
	alt: string;
	viewport: ImageViewport;
	/** Set to "high" for above-the-fold LCP images */
	fetchPriority?: "high" | "low" | "auto";
}

/**
 * Local paths under these prefixes are guaranteed to have a `.webp` sibling
 * next to every original (see `apps/platform/public/images/product-defaults/`).
 * For URLs outside of this allow-list we do NOT inject a `<source type="image/webp">`,
 * because:
 *   - External signed URLs (Firebase Storage, Cloudflare-transformed, …) do not
 *     have a matching WebP twin at the swapped-extension path, so
 *     WebP-capable browsers would pick the broken source and fail to load the
 *     image entirely instead of falling back to the original `<img src>`.
 *   - Local assets that are imported through Vite are content-hashed
 *     independently of their WebP siblings, so naive extension swapping also
 *     produces a 404.
 */
const LOCAL_WEBP_PREFIXES = ["/images/product-defaults/"];

function getLocalWebPPath(src: string): string | undefined {
	if (!LOCAL_WEBP_PREFIXES.some((prefix) => src.startsWith(prefix))) {
		return undefined;
	}
	const lastDot = src.lastIndexOf(".");
	if (lastDot === -1) return undefined;
	return `${src.substring(0, lastDot)}.webp`;
}

/**
 * Responsive product image component using Cloudflare Transform Images.
 * For external images: generates srcset via Cloudflare for optimal browser selection.
 * For local images: serves WebP via <picture> with PNG/JPG fallback when a
 *                   known WebP sibling exists; otherwise falls back to a plain
 *                   <img> so browsers don't pick a broken WebP source.
 * Sets width/height to prevent layout shift (CLS).
 */
export function ProductImage({
	src,
	alt,
	viewport,
	loading = "lazy",
	fetchPriority,
	...props
}: ProductImageProps) {
	const loaderData =
		useRouteLoaderData<Route.ComponentProps["loaderData"]>("root");
	const cloudflareBaseUrl = loaderData?.ENV?.CLOUDFLARE_TRANSFORM_IMAGES_URL;
	const urls = buildResponsiveImageUrls(src, viewport, cloudflareBaseUrl);
	const dimensions = getImageDimensions(viewport, "desktop");

	if (!urls) {
		return null;
	}

	if (!urls.srcSet) {
		const webpSrc = getLocalWebPPath(urls.src);
		const img = (
			<img
				src={urls.src}
				alt={alt}
				loading={loading}
				width={dimensions.width}
				height={dimensions.height}
				fetchPriority={fetchPriority}
				{...props}
			/>
		);
		if (!webpSrc) return img;
		return (
			<picture>
				<source srcSet={webpSrc} type="image/webp" />
				{img}
			</picture>
		);
	}

	return (
		<img
			src={urls.src}
			srcSet={urls.srcSet || undefined}
			sizes={urls.sizes || undefined}
			alt={alt}
			loading={loading}
			width={dimensions.width}
			height={dimensions.height}
			fetchPriority={fetchPriority}
			{...props}
		/>
	);
}
